/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from '../../../../../../core/components';
import { Component } from '@angular/core';
import { ErrorResponseModel } from '../../../../../../core/models';
import { LoadingService, LogService, NotifyService, UserService, UtilsService } from '../../../../../../core/services';
import { OrganisationsApiService, PortalRbacApiService } from '../../../../shared/http';
import { forkJoin, Observable, Subscriber } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-portal-users',
    templateUrl: './portal-add-edit-users.component.html',
    styleUrls: ['./portal-add-edit-users.component.scss']
})
export class PortalAddEditUsersComponent extends BaseComponent {
    /**
     * Store list of users.
     */
    public users: any[] = [];

    /**
     * Store list of groups.
     */
    public groups: any[] = [];

    /**
     * Store list of roles.
     */
    public roles: any[] = [];

    /**
     * Store the API data.
     */
    public data: any;

    /**
     * User to edit.
     */
    public userEdit: any;

    constructor(
        private activatedRoute: ActivatedRoute,
        private loadingService: LoadingService,
        private logService: LogService,
        private notifyService: NotifyService,
        private organisationsApiService: OrganisationsApiService,
        private portalRbacApiService: PortalRbacApiService,
        private router: Router,
        private userService: UserService,
        private utilsService: UtilsService
    ) {
        super();
        const id: string = this.activatedRoute.snapshot.paramMap.get('id');
        const editId: number = id ? parseInt(id, 10) : null;
        this.setUpPageData(editId);
    }

    /**
     * Submit helper function.
     * @param userValues
     */
    public submit(userValues: any): void {
        const formattedValues: any = this.formatUserVales(userValues);
        if (formattedValues) {
            if (formattedValues.id) {
                this.submitEditUser(formattedValues);
            } else {
                this.submitInviteUser(formattedValues);
            }
        } else {
            this.logService.error('PortalAddEditUsersComponent:submit', 'No user details found.');
        }
    }

    /**
     * Submit edit user.
     * @param formattedValues
     * @private
     */
    private submitEditUser(formattedValues: any): void {
        const organisationId: string = this.utilsService.duplicateObject(this.userService.getUser('organisationId'));
        this.organisationsApiService.updateAccount(organisationId, formattedValues)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.router.navigate(['../../../'], { relativeTo: this.activatedRoute }).then().catch();
                },
                error: (error: ErrorResponseModel): void => {
                    this.loadingService.loadingButtonEmitter.next(false);
                    this.logService.error('PortalAddEditUsersComponent: Submit', error.message);
                }
            });
    }

    /**
     * Submit invite user
     * @param formattedValues
     * @private
     */
    private submitInviteUser(formattedValues: any): void {
        if (!this.userEdit) {
            this.inviteUser(formattedValues)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (): void => {
                        this.router.navigate(['../../'], { relativeTo: this.activatedRoute }).then().catch();
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('PortalAddEditUsersComponent: Submit', error.message);
                    },
                    complete: (): void => {
                        this.loadingService.loadingButtonEmitter.next(false);
                    }
                });
        }
    }

    /**
     * Format the form values.
     * @param userValues
     * @private
     */
    private formatUserVales(userValues: any): any {
        return {
            id: userValues.step1?.id,
            orgID: userValues.step1?.orgId,
            email: userValues.step1?.email,
            disabled: userValues.step1?.disableUser,
            roles: userValues.step2?.roles ? this.getIds(userValues.step2?.roles) : [],
            groups: userValues.step3?.groups ? this.getIds(userValues.step3?.groups) : []
        };
    }

    /**
     * Get ids.
     * @param values
     * @private
     */
    private getIds(values: any[]): string[] {
        const filteredValues: any[] = values.filter(item => item.value);

        return filteredValues.map((item: any) => item.id?.toString()) || [];
    }

    /**
     * Set up page data.
     * @param id
     * @private
     */
    private setUpPageData(id?: string | number): void {
        const organisationId = this.userService.getUser('organisationId');
        const apiCalls: any = {
            users: this.organisationsApiService.getMembers(organisationId),
            roles: this.portalRbacApiService.getRoles(),
            groups: this.portalRbacApiService.getGroups(),
        };

        if (id) {
            apiCalls['userPatch'] = this.organisationsApiService.getAccount(organisationId, id.toString());
        }

        forkJoin(apiCalls)
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (dataGroups: any): void => {
                    this.users = dataGroups['users'];
                    this.groups = dataGroups['groups'];
                    this.roles = this.filterRoles(dataGroups['roles']);
                    dataGroups['roles'] = this.roles;

                    if (id) {
                        dataGroups['userPatch']['orgId'] = dataGroups['userPatch']['orgID'];
                        delete dataGroups['userPatch']['orgID'];
                        dataGroups['userPatch']['groups'] = this.filterGroupsOrRoles(this.groups, id.toString());
                        dataGroups['userPatch']['roles'] = this.filterGroupsOrRoles(this.roles, id.toString());
                    }

                    this.data = dataGroups;
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.error('UsersListComponent: getMembers', error.message);
                }
            });
    }

    /**
     * Filter out admin from roles.
     * @param roles
     * @private
     */
    private filterRoles(roles: any[]): any[] {
        return roles.filter(item => item.name !== 'administrator');
    }

    /**
     * Filter groups and roles to which the user belongs.
     * @param arrayGroup
     * @param userId
     * @private
     */
    private filterGroupsOrRoles(arrayGroup: any[], userId: string): any[] {
        const filteredItems: any[] = [];

        for (const arrayItem of arrayGroup) {
            if (!arrayItem.accounts) {
                continue;
            }
            const foundGroup: any = arrayItem.accounts?.find((item: string): boolean => item === userId);
            if (foundGroup) {
                filteredItems.push(arrayItem.id);
            }
        }

        return filteredItems;
    }

    /**
     * Invite new user to organisation.
     * @param user
     * @private
     */
    private inviteUser(user: any): Observable<any> {
        return new Observable((observer: Subscriber<any>): void => {
            const { organisationId, newUserObject } = this.formatMemberObject(user);
            this.notifyService.unmuteLocalNotifications();
            this.organisationsApiService.inviteMember(organisationId, newUserObject)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        observer.next(data.id);
                        observer.complete();
                    },
                    error: (error: ErrorResponseModel): void => {
                        observer.error(error);
                        observer.complete();
                    }
                });
        });
    }

    /**
     * Format the member object to submit.
     * @param user
     * @private
     */
    private formatMemberObject(user: any): any {
        const organisationId: any = this.utilsService.duplicateObject(this.userService.getUser('organisationId'));
        const newUserObject = {
            email: user.email,
            permissions: 2,
            roles: user.roles || [],
            groups: user.groups || []
        };

        return { organisationId, newUserObject };
    }
}
